import type { AppData, Employee, PolicyWarning } from './app';

// ============================================================================
// Request Headers
// ============================================================================

export interface HeaderOptions {
	id?: string | null;
	contentType?: string | false;
	accept?: string;
}

export interface FormDataFields {
	[key: string]: string | Blob | File;
}

// ============================================================================
// API Response Types
// ============================================================================

export interface TokenRefreshResponse {
	access_token: string;
}

export interface PolicyUploadResponse extends AppData {
	policy_upload_form_id: string;
	access_token: string;
	// ``temp_redirect_url`` is inherited from ``AppData`` as an optional
	// ``string``. The backend schema is ``str | None``: only the
	// no-policy ``submitWithoutFile`` path populates it with a frontend
	// aanbod link; upload/submit responses omit it. Callers MUST treat
	// it as optional and only redirect when truthy.
}

export interface ActivationResponse {
	activation_redirect_url: string | null;
}

// ============================================================================
// Premium Variables Payloads
// ============================================================================

/**
 * Employee data payload for premium variable updates
 * @property source - Data source: 'client' (user input), 'import' (CSV), or 'policy' (extracted)
 * @property employee_list - Array of employee records
 */
export interface EmployeePayload {
	source: 'client' | 'import' | 'policy';
	employee_list: Employee[];
}

/**
 * Absence data payload for premium variable updates
 * @property source - Data source: 'client' (user input) or 'policy' (extracted)
 * @property rates_by_year - Absence rates keyed by year
 */
export interface AbsencePayload {
	source: 'client' | 'policy';
	rates_by_year: Record<string, number>;
}

/**
 * Coverage data payload for premium variable updates
 * @property sick_leave_short_term - Coverage settings for sick leave short term
 */
export interface CoveragePayload {
	source?: 'client' | 'current_policy';
	sick_leave_short_term: {
		source?: 'client' | 'current_policy';
		coverage_percentage_first_year_first_half: number;
		coverage_percentage_first_year_second_half: number;
		coverage_percentage_second_year_first_half: number;
		coverage_percentage_second_year_second_half: number;
		employer_costs: number;
		waiting_period: number;
	};
}

/**
 * Combined premium variables payload
 * Can include employee data, absence data, coverage data, or any combination
 */
export interface PremiumVarsPayload {
	employees?: EmployeePayload;
	absence?: AbsencePayload;
	coverage?: CoveragePayload;
}

// ============================================================================
// Company Information Payloads
// ============================================================================

/**
 * Chamber of Commerce (KvK) number update payload
 */
export interface CocPayload {
	coc: string;
}

/**
 * Company search request payload
 */
export interface CocLookupPayload {
	company_name: string;
	city?: string;
}

/**
 * Single company search result
 */
export interface CocSearchResultItem {
	coc: string;
	company_name: string;
	city: string;
	address?: string;
	zip_code?: string;
}

/**
 * Company search response
 */
export interface CocSearchResponse {
	results: CocSearchResultItem[];
}

/**
 * KVK company search request payload
 */
export interface KVKSearchPayload {
	name: string;
	page?: number;
	results_per_page?: number;
}

/**
 * Single KVK company from search results
 */
export interface KVKCompany {
	bedrijfsnaam: string;
	kvk_nummer: string;
	plaats: string;
	straatnaam?: string;
}

/**
 * KVK company search response
 */
export interface KVKSearchResult {
	companies: KVKCompany[];
	total: number;
	page: number;
}

/**
 * Sector code update payload
 */
export interface SectorCodePayload {
	sector_code: number;
}

/**
 * Wage info update payload
 */
export interface WageInfoPayload {
	wage_sum: number;
}

/**
 * Premium percentage update payload
 */
export interface PremiumPercentagePayload {
	current_premium_percentage: number | null;
}

/** Minimal quote list payload returned by some POST/PATCH routes (not full AppData). */
export interface QuoteListStatusResponse {
	status: string;
	expected_num_quotes?: number | null;
}

// ============================================================================
// Application Payloads
// ============================================================================

// ============================================================================
// Quote by Product Type
// ============================================================================

/**
 * All product types supported by the /quotes/{product_type} endpoints.
 */
export enum SupportedProductType {
	SICK_LEAVE_SHORT_TERM = 'sick_leave_short_term',
	SICK_LEAVE_LONG_TERM_WGA_GAP_EXTENDED = 'sick_leave_long_term_wga_gap_extended',
	SICK_LEAVE_LONG_TERM_WGA_GAP = 'sick_leave_long_term_wga_gap',
	SICK_LEAVE_LONG_TERM_WIA_EXCESS = 'sick_leave_long_term_wia_excess',
	WGA_EIGEN_RISICO_VERZEKERING = 'wga_eigen_risico_verzekering',
	WIA_35_MIN_VAST = 'wia_35_min_vast',
	WIA_35_MIN_VARIABEL = 'wia_35_min_variabel',
	WIA_AANVULLING = 'wia_aanvulling',
	WIA_AANVULLING_VARIABEL = 'wia_aanvulling_variabel',
	IVA_AANVULLING = 'iva_aanvulling',
	ZIEKTEWET_EIGENRISICODRAGER = 'ziektewet_eigenrisicodrager',
	ARBODIENSTVERLENING = 'arbodienstverlening',
	OCCUPATIONAL_HEALTH_SERVICE = 'occupational_health_service'
}

/**
 * A single quote item returned by the /quotes/{product_type} endpoints.
 */
export interface QuoteItem {
	quote_id: string;
	premium_rate_percentage: number;
	amount: number | null;
	total: number | null;
	saving_percentage: number | null;
	saving_amount: number | null;
	insurer: string;
	insurer_name: string;
	image_key: string | null;
}

/**
 * Response from GET/POST/PATCH /quotes/{product_type}
 *
 * @remarks
 * `relevant_product_types` is the per-request list of product types the
 * frontend should render. It is computed by the backend by intersecting the
 * label's available products with each product's insured-interest calculation
 * (e.g. WIA Excedent only appears once an employee exceeds MAX_SV_LOON).
 * Use this field instead of the static `config.products` map, which has been
 * removed from the `/config` response (backend PR #984).
 */
export interface QuoteListResponse {
	status: string;
	expected_num_quotes: number;
	policy_warnings?: PolicyWarning[];
	quotes: Record<string, QuoteItem[]>;
	relevant_product_types?: SupportedProductType[];
	// Form-level fields the backend includes in this payload (same shape the full
	// GET / AppData carries). Declared so consumers like `mergeStreamSnapshot` can
	// overlay them onto AppData without casting off the snapshot.
	company_info?: AppData['company_info'];
	current_policy?: AppData['current_policy'];
	premium_variables?: AppData['premium_variables'];
	unreadable_reason?: AppData['unreadable_reason'];
	has_current_policy_document?: AppData['has_current_policy_document'];
}

/**
 * Coverage override payload for /quotes/{product_type} requests.
 * Keys are product type strings; values are product-specific coverage objects.
 */
export type ProductTypeCoveragePayload = {
	sick_leave_long_term_wia_excess?: { dekkingspercentage: string };
	sick_leave_long_term_wga_gap_extended?: Record<string, never>;
	sick_leave_long_term_wga_gap?: Record<string, never>;
	[key: string]: unknown;
};

// ============================================================================
// Application Payloads
// ============================================================================

/**
 * Application form data update payload
 * All fields are optional to support partial updates
 */
export interface ApplicationUpdatePayload {
	quote_id?: string | null;
	initials?: string | null;
	prefix?: string | null;
	lastName?: string | null;
	email?: string | null;
	phone?: string | null;
	arboService?: boolean;
	arbodienstverlener?: string | null;
	agreeToTerms?: boolean | null;
	agreeToCancel?: boolean | null;
	hasNoLegalIssues?: boolean | null;
	hasNoInsuranceIssues?: boolean | null;
	hasNoBankruptcyIssues?: boolean | null;
	selected_sick_leave_short_term_quote_id?: string | null;
	selected_occupational_health_service_quote_id?: string | null;
	selected_wga_gap_extended_quote_id?: string | null;
	selected_wga_gap_quote_id?: string | null;
	selected_wia_excess_quote_id?: string | null;
	selected_wia_excess_dekkingspercentage?: string | null;
}

/**
 * Response from GET /policy-upload-forms/{id}/application
 * Contains pre-filled contact details from the contact form
 * Note: API returns snake_case, frontend uses camelCase
 */
export interface ApplicationResponse {
	selected_sick_leave_short_term_quote_id?: string | null;
	selected_occupational_health_service_quote_id?: string | null;
	selected_wga_gap_extended_quote_id?: string | null;
	selected_wga_gap_quote_id?: string | null;
	selected_wia_excess_quote_id?: string | null;
	selected_wia_excess_dekkingspercentage?: string | null;
	initials?: string | null;
	prefix?: string | null;
	last_name?: string | null;
	phone?: string | null;
	email?: string | null;
	has_no_legal_issues?: boolean | null;
	has_no_insurance_issues?: boolean | null;
	has_no_bankruptcy_issues?: boolean | null;
	agree_to_terms?: boolean | null;
	agree_to_cancel?: boolean | null;
}

/**
 * Contact form submission payload
 */
export interface ContactPayload {
	email?: string;
	phone?: string;
	name?: string;
	message?: string;
	captchaSolution: string;
}

// ============================================================================
// Employee Import Types
// ============================================================================

/**
 * Response from `POST /policy-upload-forms/{id}/employees/import` (CSV/XLS import).
 * Distinct from {@link ExtractionResponse}, which wraps extraction status + result.
 */
export interface EmployeeImportResponse {
	status: 'processing' | 'completed' | 'failed';
	employee_list?: Employee[];
	error?: string;
}
